import React, { useState } from 'react';
import { Typography, IconButton } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import styles from './OfferCard.module.scss';

const OfferCard = ({ offer, onClick, currentUser, onEdit, onDelete }) => {
  const [imageError, setImageError] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isExpiring = () => {
    if (!offer.validUntil) return false;
    const daysUntil = Math.ceil((new Date(offer.validUntil) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntil <= 7 && daysUntil > 0;
  };

  // Access control: admin or owner can edit/delete
  const isAdmin = ['LEADER', 'MANAGER'].includes(currentUser?.type);
  const isOwner = offer.userId === currentUser?.id;
  const canEditDelete = isAdmin || isOwner;

  const handleEdit = (e) => {
    e.stopPropagation(); // Prevent card click
    onEdit?.(offer);
  };

  const handleDelete = (e) => {
    e.stopPropagation(); // Prevent card click
    onDelete?.(offer);
  };

  // Get image URL - use full backend URL from environment
  const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:3001/api';
  const imageUrl = `${apiBase}/offer-images/${offer.id}`;

  return (
    <div className={styles.card} onClick={onClick}>
      {/* Action Buttons - only for owner/admin */}
      {canEditDelete && (
        <div className={styles.cardActions}>
          <IconButton 
            size="small" 
            className={styles.editBtn}
            onClick={handleEdit}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            className={styles.deleteBtn}
            onClick={handleDelete}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      )}

      {/* Discount Badge */}
      {offer.discount && (
        <div className={styles.discountBadge}>
          {offer.discount}
        </div>
      )}

      {/* Image or Placeholder */}
      <div className={styles.imageContainer}>
        {!imageError ? (
          <img 
            src={imageUrl} 
            alt={offer.title}
            className={styles.image}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className={styles.imagePlaceholder}>
            <span className={styles.placeholderIcon}>🎁</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Category */}
        {offer.category && (
          <span className={styles.category}>{offer.category}</span>
        )}

        {/* Title */}
        <Typography variant="h6" className={styles.title}>
          {offer.title}
        </Typography>

        {/* Description */}
        <Typography variant="body2" className={styles.description}>
          {offer.description?.length > 100 
            ? offer.description.substring(0, 100) + '...'
            : offer.description
          }
        </Typography>

        {/* Meta Info */}
        <div className={styles.meta}>
          {offer.company && (
            <div className={styles.metaItem}>
              <BusinessIcon />
              <span>{offer.company.name}</span>
            </div>
          )}
          
          {offer.validUntil && (
            <div className={`${styles.metaItem} ${isExpiring() ? styles.expiring : ''}`}>
              <CalendarTodayIcon />
              <span>Până la {formatDate(offer.validUntil)}</span>
            </div>
          )}
        </div>

        {/* Author */}
        <div className={styles.author}>
          <span className={styles.authorAvatar}>
            {offer.user?.firstName?.[0]}{offer.user?.lastName?.[0]}
          </span>
          <span className={styles.authorName}>
            {offer.user?.firstName} {offer.user?.lastName}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OfferCard;

